import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Banner from '@/common/Banner'
import useCartItems from '@order/hooks/useCart'
import useDeals from '@order/hooks/useDeals'
import { useDealSelect } from '@order/hooks/useDealTypeModal'
import { useOrderState } from '@order/hooks/useOrderState'
import useStartOrder from '@order/hooks/useStartOrder'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import useSession from 'hooks/useSession'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, Pressable } from 'react-native'
import { SelectSchema } from 'features/config/ConfigConditionSchemas'

const DealBanner = ({ config }: { config: SelectSchema<'banner'> }) => {
  const { t } = useTranslation(['banner'])

  const cart = useCartItems()
  const { selectDeal } = useDealSelect()
  const resetOrder = useOrderState((s) => s.reset)
  const resetDeals = useSession((s) => s.resetDeals)
  const startOrder = useStartOrder()
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const deals = useDeals({ hidden: true, filterCards: false })

  const deal = useMemo(() => {
    return deals.find((deal) => deal.code === config.deal)
  }, [deals, config.deal])

  const show = useMemo(() => {
    const deals = cart.list.flatMap((item) =>
      item.result?.orders.map((order) => order.data.strategy_ref)
    )

    return config.require.some((deal) => deals.includes(deal))
  }, [cart.list, config.require])

  const handlePress = useCallback(() => {
    resetOrder()
    resetDeals(true)
    selectDeal({
      deal,
      bundle: 'base',
      onSelected: () => {
        startOrder()
      },
    })
  }, [deal, selectDeal, resetOrder, resetDeals, startOrder])

  if (!show || !deal) return null

  return (
    <Pressable
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      onPress={handlePress}
      className="mt-8 px-8"
    >
      <Banner
        animation={animation}
        blurMode={Platform.select({
          default: 'backdropFilter',
          web: 'blurRadius',
        })}
        title={t(`banner:${config.key}.title`)}
        desc={t(`banner:${config.key}.desc`)}
        image={require('@/images/intro/banner-front.png')}
      />
    </Pressable>
  )
}

const CheckoutBanner = () => {
  const config = useConfigConditionValue('banner:checkout', 'banner')
  if (!config) return null

  return <DealBanner config={config} />
}

export default CheckoutBanner
